import { FaCheck } from "react-icons/fa";
import { FaHourglass } from "react-icons/fa";
import { MdRepeat } from "react-icons/md";
import {
  useMenu,
  useTasks,
  useTimer,
  useUser,
  useUI,
  useTaskActions,
} from "../hooks/index.js";
import { PLUS } from "../helpers/subscriptionTiers";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  navigateToProjectAtom,
  completeTaskWithChildrenAtom,
} from "../store/actions.js";
import { allTasksAtom, recurringRulesAtom } from "../store/atoms/taskAtoms.js";

const BubbleMenu = () => {
  const { contextMenuPosition, setShowContextMenu, setOverlayContent } = useMenu();
  const { selectedTask, setSelectedTask } = useTasks();
  const { preferences, subscriptionTier } = useUser();
  const { timer, setTimer, setElapsedTime, setActiveBubble } = useTimer();
  const { isMobile } = useUI();
  const { completeTask, updateTask, duplicateTask, deleteRecurringTask } = useTaskActions();
  const [, navigateToProject] = useAtom(navigateToProjectAtom);
  const completeTaskWithChildren = useSetAtom(completeTaskWithChildrenAtom);
  const allTasks = useAtomValue(allTasksAtom);
  const recurringRules = useAtomValue(recurringRulesAtom);

  const getTaskHierarchyLevel = (task) => {
    if (!task || !task.parent_id) return 0;

    let level = 1;
    let parentId = task.parent_id;

    while (parentId && level < 3) {
      let parentTask = null;
      for (let i = 0; i < allTasks.length; i++) {
        if (allTasks[i].task_id === parentId) {
          parentTask = allTasks[i];
          break;
        }
      }
      if (!parentTask) break;
      parentId = parentTask.parent_id;
      level++;
    }

    return level;
  };

  const taskHierarchyLevel = selectedTask
    ? getTaskHierarchyLevel(selectedTask)
    : 0;

  const hasChildren = selectedTask?.hasChildren || false;

  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  const startTimer = (initialTime = 0) => {
    if (isNaN(initialTime)) initialTime = 0;
    const startTime = Date.now() - initialTime * 1000;
    const timerInterval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsedSeconds);
    }, 1000);
    setTimer(timerInterval);
  };

  const handleTimer = () => {
    setActiveBubble(selectedTask);
    startTimer(selectedTask.minutes ? selectedTask.minutes * 60 : 0);
    closeContextMenu();
  };

  const handleComplete = () => {
    completeTask(selectedTask);
    closeContextMenu();
  };

  const handleCompleteAll = async () => {
    try {
      const success = await completeTaskWithChildren({
        taskId: selectedTask.task_id,
        completeChildren: true,
      });
      if (success) {
        closeContextMenu();
      }
    } catch (error) {
      console.error("Error completing all tasks:", error);
    }
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    try {
      const updatedTask = {
        task_id: selectedTask.task_id,
        task_name: selectedTask.task_name,
        description: selectedTask.description,
        category: selectedTask.category,
        effort: selectedTask.effort,
        impact: selectedTask.impact,
        due_date: selectedTask.due_date,
        status: selectedTask.status,
      };

      const success = await updateTask(updatedTask);
      if (success) {
        closeContextMenu();
      } else {
        console.error("Failed to update task");
      }
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  const handleDuplicate = async () => {
    try {
      const success = await duplicateTask(selectedTask.task_id);
      if (success) {
        closeContextMenu();
      }
    } catch (error) {
      console.error("Error duplicating task:", error);
    }
  };

  const handleCreateSubtasks = () => {
    navigateToProject(selectedTask.task_id);
    closeContextMenu();
  };

  const isRecurring = recurringRules.some(
    (r) => r.source_task_id === selectedTask?.task_id
  );
  const isRecurringCopy = !isRecurring && recurringRules.some(
    (r) => r.source_task_id === selectedTask?.recurring_source_id
  );

  const handleSetRecurring = () => {
    closeContextMenu();
    setOverlayContent("recurring");
  };

  const handleStopRecurring = async () => {
    closeContextMenu();
    await deleteRecurringTask(selectedTask.recurring_source_id);
  };

  return (
    <div
      className="context-menu"
      style={{
        userSelect: "none",
        position: "absolute",
        top: `${contextMenuPosition.y}px`,
        left: `${contextMenuPosition.x}px`,
        zIndex: 1000,
        paddingTop: isMobile ? "45px" : "10px",
      }}
    >
      <form onSubmit={handleSaveTask}>
        <div className="button-group bubble-top-buttons">
          {!selectedTask?.hasChildren && (
            <button
              type="button"
              onClick={(e) => {
                handleSaveTask(e);
                setTimeout(() => {
                  handleDuplicate();
                }, 200);
              }}
            >
              Duplicate
            </button>
          )}
          {isRecurringCopy ? (
            <button
              type="button"
              onClick={handleStopRecurring}
              className="recurring-indicator-btn"
            >
              <MdRepeat style={{ fontSize: "14px" }} />
              Stop recurring
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSetRecurring}
              className="recurring-indicator-btn"
            >
              <MdRepeat style={{ fontSize: "14px" }} />
              {isRecurring ? "Edit recurring" : "Set recurring"}
            </button>
          )}
          {taskHierarchyLevel < 2 && (
            <button type="button" onClick={handleCreateSubtasks}>
              Create subtasks
            </button>
          )}
          {hasChildren && (
            <button type="button" onClick={handleCompleteAll}>
              Complete all
            </button>
          )}
        </div>

        {isMobile && (
          <button
            className="top-button complete"
            type="button"
            onClick={handleComplete}
          >
            <FaCheck style={{ fontSize: "16px" }} />
          </button>
        )}

        {!timer && isMobile && subscriptionTier >= PLUS && (
          <button
            className="top-button timer"
            type="button"
            onClick={handleTimer}
          >
            <FaHourglass style={{ fontSize: "16px" }} />
          </button>
        )}

        <label>
          Task:
          <input
            type="text"
            defaultValue={selectedTask.task_name}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                task_name: e.target.value,
              })
            }
          />
        </label>
        <label>
          Long Description / Notes:
          <input
            type="text"
            defaultValue={selectedTask.description}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                description: e.target.value,
              })
            }
          />
        </label>
        <label>
          Category:
          <select
            defaultValue={selectedTask.category}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                category: e.target.value,
              })
            }
          >
            {preferences
              .filter((pref) => pref.category !== 0) // exclude font preference row
              .map((pref) => (
                <option key={pref.category} value={pref.category}>
                  {pref.name
                    ? `${pref.name} (${pref.color})`
                    : `Not Set (${pref.color})`}
                </option>
              ))}
          </select>
        </label>
        <label>
          Effort:
          <select
            defaultValue={selectedTask.effort}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                effort: e.target.value,
              })
            }
          >
            <option value="20">Low</option>
            <option value="40">Medium</option>
            <option value="50" disabled hidden>
              Not Specified
            </option>
            <option value="60">High</option>
            <option value="80">Taxing</option>
          </select>
        </label>
        <label>
          Impact:
          <select
            defaultValue={selectedTask.impact}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                impact: e.target.value,
              })
            }
          >
            <option value="20">Limited</option>
            <option value="40">Moderate</option>
            <option value="50" disabled hidden>
              Not Specified
            </option>
            <option value="60">High</option>
            <option value="80">Transformative</option>
          </select>
        </label>
        <label>
          Deadline:
          <input
            type="date"
            defaultValue={
              selectedTask.due_date
                ? new Date(selectedTask.due_date).toISOString().substring(0, 10)
                : ""
            }
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                due_date: e.target.value || null,
              })
            }
          />
        </label>
        <label>
          Status:
          <select
            defaultValue={selectedTask.status}
            onChange={(e) =>
              setSelectedTask({
                ...selectedTask,
                status: e.target.value,
              })
            }
          >
            <option value="pending">Not Started</option>
            <option value="working">In Progress</option>
            <option value="stuck">Stuck</option>
            <option value="complete" disabled hidden>
              Complete
            </option>
          </select>
        </label>
        <div className="button-group">
          <button type="submit">Save</button>
        </div>
      </form>
    </div>
  );
};

export default BubbleMenu;
